'use client';

// this variant of client was required because we could not load the async server component in a drawer which was nested in multiple client components, it previously worked in the buySellButton because the actual definition and lazy loading was done in the desktopHeader component which was SSR

import { FC, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { css } from '@bts-web/utils-style-engine';
import {
  getPortfolioAssets,
  useHandleGqlErrorsWithSnackbar,
} from '@bts-web/core-features/common';
import {
  GetPaginatedAssetsForTransfersQuery$data,
  PortfolioAssetsViewQuery$data,
} from '@bts-web/data-layer/server';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { getCryptoTransferTranslations } from '../../utils/getCryptoTransferTranslations';
import { getPaginatedAssetsForTransfer } from '../../../common/gqlActions/getPaginatedAssetsForTransfer';
import { getCryptoTransfersVASPList } from '../../../common/gqlActions/getCryptoTransfersVASPList';
import { getPaginatedAssetsForTransferInitialQueryParams } from './utils';
import { TransfersSectionVariants } from '../../types';
import { TransfersAssetListContextProvider } from '../TransfersAssetListContext/TransfersAssetListContext';
import { TransfersParent } from '../TransfersParent/TransfersParent';
import { TransfersSkeleton } from '../TransfersParent/TransfersSkeleton';
import { useDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';

const loadAssetsWithParams = async (
  transferType: TransfersSectionVariants,
): Promise<
  | Awaited<ReturnType<typeof getPaginatedAssetsForTransfer>>
  | Awaited<ReturnType<typeof getPortfolioAssets>>
> => {
  if (transferType === TransfersSectionVariants.Send) {
    const portfolioAssetsResponse = await getPortfolioAssets({
      period: 'DAY',
    });

    return portfolioAssetsResponse;
  }

  const assetsResponse = await getPaginatedAssetsForTransfer(
    getPaginatedAssetsForTransferInitialQueryParams,
  );

  return assetsResponse;
};

const TransfersLoaderClient: FC<{
  transferType: TransfersSectionVariants;
}> = ({ transferType }) => {
  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { closeDrawer } = useDrawerContext();

  const { t } = useClientTranslation();

  const [loading, setLoading] = useState(true);

  const [assetsData, setAssetsData] = useState<
    | Awaited<ReturnType<typeof getPaginatedAssetsForTransfer>>
    | Awaited<ReturnType<typeof getPortfolioAssets>>
    | null
  >(null);

  const [walletHostsData, setWalletHostsData] = useState<
    | Awaited<
        ReturnType<typeof getCryptoTransfersVASPList>
      >['data']['cryptoTransfersVASP']
    | null
  >(null);

  const fetchAndBindData = async () => {
    try {
      const [assetsResponse, walletHostsResponse] = await Promise.all([
        loadAssetsWithParams(transferType),
        getCryptoTransfersVASPList(),
      ]);

      if (
        transferType === TransfersSectionVariants.Deposit &&
        !(assetsResponse?.data as GetPaginatedAssetsForTransfersQuery$data)
          ?.assets
      ) {
        throw new Error('Failed to fetch assets for deposit');
      }

      if (
        transferType === TransfersSectionVariants.Send &&
        !(assetsResponse?.data as PortfolioAssetsViewQuery$data)?.portfolio
      ) {
        throw new Error('Failed to fetch portfolio assets for withdrawal');
      }

      if (!walletHostsResponse?.data?.cryptoTransfersVASP) {
        throw new Error('Failed to fetch wallet hosts for transfers');
      }

      setWalletHostsData(walletHostsResponse?.data?.cryptoTransfersVASP);

      setAssetsData(assetsResponse);
    } catch (error) {
      processErrors([
        ((error as Error)?.message as string) || error,
      ] as string[]);

      closeDrawer();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndBindData();
  }, []);

  const translations = getCryptoTransferTranslations(t as TFunction);

  if (!assetsData || !walletHostsData || loading) {
    return (
      <div className={css({ p: 'small' })}>
        <TransfersSkeleton />
      </div>
    );
  }

  return (
    <TransfersAssetListContextProvider
      initialData={assetsData?.data}
      walletHosts={walletHostsData}
    >
      <TransfersParent
        translations={translations}
        transferType={transferType}
      />
    </TransfersAssetListContextProvider>
  );
};

export { TransfersLoaderClient };
